import { useBookingContext } from '../BookingContext'
import { Button } from '@w/components/ui/button'
import { Card, CardContent } from '@w/components/ui/card'
import { format } from 'date-fns'
import { CheckCircle2, Calendar, MapPin, User, Mail, Phone, Sparkles, RefreshCcw } from 'lucide-react'

export default function SuccessStep() {
  const {
    config,
    selectedService,
    selectedDateTime,
    customerInfo,
    confirmationData,
    reset,
    configurationMode,
  } = useBookingContext()

  // Configuration mode: Show success layout with placeholders
  if (configurationMode) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <CheckCircle2 className="w-16 h-16 text-white" />
          </div>
          <h2 className="text-4xl font-bold mb-3 bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent">
            Booking Confirmed!
          </h2>
          <p className="text-lg text-muted-foreground">
            {config.widgetConfig.success_message || "Thank you for booking with us! We'll see you soon."}
          </p>
        </div>

        <Card className="mb-8">
          <CardContent className="p-6 space-y-4">
            <div>
              <h3 className="text-sm text-muted-foreground mb-1">Confirmation #</h3>
              <p className="font-mono font-semibold text-lg">[CONFIRM-123]</p>
            </div>
            <div>
              <h3 className="text-sm text-muted-foreground mb-1">Service</h3>
              <p className="font-medium">[Service Name]</p>
            </div>
            <div>
              <h3 className="text-sm text-muted-foreground mb-1">When</h3>
              <p className="font-medium">[Date] at [Time]</p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center">
          <Button disabled size="lg" className="gap-2">
            <RefreshCcw className="w-4 h-4" />
            Book Another Appointment
          </Button>
        </div>
      </div>
    )
  }

  if (!confirmationData) {
    return <div className="text-center text-muted-foreground">No confirmation data available</div>
  }

  // Generate confirmation number
  const appointmentId = confirmationData.appointment?.id || 'DEMO'
  const confirmationNumber = confirmationData.preview_mode
    ? `DEMO-${appointmentId.substring(0, 6).toUpperCase()}`
    : `BK-${appointmentId.substring(appointmentId.length - 6).toUpperCase()}`

  // Format date for display
  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return format(date, 'EEEE, MMMM d, yyyy')
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Success Header */}
      <div className="text-center mb-10">
        <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg animate-in zoom-in duration-500">
          <CheckCircle2 className="w-16 h-16 text-white" />
        </div>

        <h2 className="text-4xl font-bold mb-3 bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent">
          Booking Confirmed!
        </h2>
        <p className="text-lg text-muted-foreground">
          {confirmationData.preview_mode
            ? '🎭 Demo booking successful! (No real appointment created)'
            : (config.widgetConfig.success_message || "Your appointment has been scheduled. You'll receive a confirmation email shortly.")}
        </p>
      </div>

      {/* Confirmation Number Badge */}
      <Card className="mb-8 border-2 border-primary/20 bg-primary/5">
        <CardContent className="p-6 text-center">
          <p className="text-sm text-muted-foreground mb-2">Confirmation Number</p>
          <p className="text-3xl font-bold text-primary tracking-wide">{confirmationNumber}</p>
        </CardContent>
      </Card>

      {/* Details Grid */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Service */}
        {selectedService && (
          <Card className="transition-all duration-200 hover:shadow-md hover:scale-[1.02]">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-muted-foreground mb-1">Service</h3>
                  <p className="font-semibold text-lg">{selectedService.name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Date & Time */}
        {selectedDateTime && (
          <Card className="transition-all duration-200 hover:shadow-md hover:scale-[1.02]">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-muted-foreground mb-1">Date & Time</h3>
                  <p className="font-medium">{formatDate(selectedDateTime.date)}</p>
                  <p className="text-sm text-muted-foreground">{selectedDateTime.time}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Address */}
        {selectedDateTime && (
          <Card className="transition-all duration-200 hover:shadow-md hover:scale-[1.02]">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-muted-foreground mb-1">Service Address</h3>
                  <p className="font-medium">{selectedDateTime.address}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contact */}
        {customerInfo && (
          <Card className="transition-all duration-200 hover:shadow-md hover:scale-[1.02]">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-orange-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-muted-foreground mb-1">Contact</h3>
                  <p className="font-medium mb-2">{customerInfo.name}</p>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Mail className="w-3 h-3" />
                      {customerInfo.email}
                    </p>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Phone className="w-3 h-3" />
                      {customerInfo.phone}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* What's Next */}
      <Card className="mb-8 bg-blue-50/50 border-blue-200">
        <CardContent className="p-6">
          <h4 className="font-semibold mb-3 flex items-center gap-2 text-blue-900">
            <CheckCircle2 className="w-5 h-5" />
            What's Next?
          </h4>
          <ul className="space-y-2 text-sm text-blue-900/80">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">•</span>
              <span>You'll receive a confirmation email shortly</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">•</span>
              <span>We'll contact you 24 hours before your appointment</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">•</span>
              <span>Questions? Contact {config.businessInfo.name} directly</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Book Another Button */}
      <div className="text-center">
        <Button onClick={reset} size="lg" className="gap-2 px-8" variant="outline">
          <RefreshCcw className="w-4 h-4" />
          Book Another Appointment
        </Button>
      </div>
    </div>
  )
}
